import { ReservationTimeState, ResourceCategoryType } from '@mews-commander/data-access';
import { Resource, ResourceStateReasonClassification } from '@mews-commander/resources';
import { Customer, ResourceBlock, SimpleDataReservation } from '@mews-commander/types';
import { CustomerClassifications } from '@mews-commander/legacy-data-structures';
import { resourceComparator } from '../constants';

export interface WithTimelineCoordinates {
    maxX: number;
    maxY: number;
    minX: number;
    minY: number;
}

interface TimelineNode {
    depth: number;
    isRoot: boolean;
    isLeaf: boolean;
    childCount: number;
}

interface WithResourceCategoryId {
    resourceCategoryId: string;
}

export type EnhancedResource = Resource & TimelineNode & WithTimelineCoordinates & WithResourceCategoryId;

const rootNode = {};
const rootId = 'ROOT';

const constructHash = (records: Resource[]) => {
    const hashMap: Record<string, Resource | typeof rootNode> = {};
    const hashTree: Record<string, string[]> = {
        [rootId]: [],
    };
    records.forEach((node: Resource) => {
        const { id } = node;
        hashMap[id] = node;
    });
    records.forEach((node: Resource) => {
        const { id, parentId: nodeParentId } = node;

        const hasParent = nodeParentId != null && hashMap[nodeParentId] != null;
        const parentId = hasParent ? nodeParentId : rootId;

        hashTree[id] ??= [];
        hashTree[parentId] ??= [];
        hashTree[parentId].push(id);
    });
    return { hashMap, hashTree };
};

export function constructResourcesTree(resources: Resource[] = []) {
    const resourcesSpaces = resources.sort(resourceComparator);
    const { hashMap, hashTree } = constructHash(resourcesSpaces);
    const enhancedResources: Record<string, EnhancedResource> = {};
    let maxDepth = 0;
    let leafCount = 0;

    function traverseTree(nodeId: string, depth: number, minY: number) {
        const node = hashMap[nodeId];
        const deeperDepth = depth + 1;
        const children = hashTree[nodeId];
        const isLeaf = children.length === 0;

        if (isLeaf) {
            leafCount++;
        }
        if (depth > maxDepth) {
            maxDepth = depth;
        }

        const childCount = children.reduce(
            (childSum, childId: string) => childSum + traverseTree(childId, deeperDepth, minY + childSum),
            0
        );
        const childCountMin = Math.max(childCount, 1);

        const timelineData = {
            isRoot: depth === 0,
            childCount,
            isLeaf,
            depth,
            maxY: minY + childCountMin,
            maxX: deeperDepth,
            minX: depth,
            minY,
        };

        enhancedResources[nodeId] = { ...node, ...timelineData } as EnhancedResource;

        return childCountMin;
    }

    hashMap[rootId] = rootNode;
    traverseTree(rootId, -1, 0);

    const rankedSortedResources = resourcesSpaces.map((resourceSpace) => ({
        ...enhancedResources[resourceSpace.id],
        ...(resourceSpace as WithResourceCategoryId),
    }));

    return { maxDepth, leafCount, resources: rankedSortedResources, lookup: enhancedResources };
}

const getChildrenIds = (resourceIds: string[], resources: Record<string, Array<Resource>>) =>
    resourceIds.flatMap((resourceId) => resources[resourceId] ?? []).map(({ id }) => id);

export const findResourcesChildrenIds = (
    parentIds: string[],
    resources: Record<string, Array<Resource>>,
    childrenIds: string[] = []
): string[] => {
    const foundChildrenIds = getChildrenIds(parentIds, resources);

    if (foundChildrenIds.length === 0) {
        return childrenIds;
    }

    return findResourcesChildrenIds(foundChildrenIds, resources, [...childrenIds, ...foundChildrenIds]);
};

export interface CommonResourceUseProps extends WithTimelineCoordinates {
    displaceY: number;
    isMoveable: boolean;
    isReservation: boolean;
    serviceName: string | null;
    resource: ResourceUseResource;
    customerId: string | null;
}

export interface ResourceUseCluster {
    startUtc: string;
    endUtc: string;
    assignedResourceId: string;
    serviceIds: string[];
    serviceNames: string[];
    resourceUseIds: string[];
    isMultipleServices: boolean;
    minX: number | null;
    maxX: number | null;
}

export interface ResourceUseResource extends EnhancedResource {
    stateReasonClassification: ResourceStateReasonClassification;
    normalBedCount: number;
    extraBedCount: number;
    resourceCategoryId: string;
    resourceCategoryName: string;
    resourceCategoryShortName: string;
    resourceCategoryOrdering: number;
    resourceCategoryType: ResourceCategoryType;
    isSlot: boolean;
    floorNumber: string;
    number: string;
    serviceId: string | undefined;
}

export interface ResourceUsePlaceholder {
    x1: number;
    x2: number;
    y1: number;
    y2: number;
    startUtc: string;
    endUtc: string;
}

export interface ReservationResourceUse extends SimpleDataReservation, CommonResourceUseProps {
    reservationTimeState: ReservationTimeState;
    state: number;
    timeState: number;
    customerId: string;
    customer: Customer & {
        classificationsDecoded: typeof CustomerClassifications.enumValues;
    };
}

export interface ResourceBlockResourceUse extends ResourceBlock, CommonResourceUseProps {
    releasedUtc: string;
}
